import Flickity from 'flickity'
import { ComponentChildren } from 'preact'
import { createPortal } from 'preact/compat'
import { useCallback, useEffect, useRef } from 'preact/hooks'
import debounce from 'lodash.debounce'

const defaultOptions: Flickity.Options = {
  contain: true,
  resize: false
}

// Annoying little fix for the bug where the slider doesn't resize properly with dynamically sized elements when the window is resized
const resizeListener = (flickity: Flickity) => {
  const minHeightElements = flickity.slider.querySelectorAll('.min-h-full')
  minHeightElements.forEach((element: Element) => {
    element.classList.remove('min-h-full')
  })
  flickity.resize()
  flickity.reloadCells()
  minHeightElements.forEach((element: Element) => {
    element.classList.add('min-h-full')
  })
}

export function useFlickity(target: Element, options: Flickity.Options, deps: any[] = []) {
  const flickityRef = useRef<Flickity | null>(null)
  const sliderRef = useRef<Element | null>(null)
  const flickityOptions = { ...defaultOptions, ...options }

  useEffect(() => {
    if (!target) return
    flickityRef.current = new Flickity(target, options = { ...flickityOptions })
    const handleResize = debounce(() => {
      if (flickityRef.current) {
        resizeListener(flickityRef.current)
      }
    }, 100)
    window.addEventListener('resize', handleResize)

    sliderRef.current = target.querySelector('.flickity-slider')
    return () => {
      flickityRef.current?.destroy()
      window.removeEventListener('resize', handleResize)
    }
  }, deps)

  const SliderPortal = useCallback(
    ({ children }: {children: ComponentChildren}) => {
      if (!target) return children
      return createPortal(children, target)
    },
    [target]
  )

  return {
    flickity: flickityRef.current,
    SliderPortal
  }
}
